import { createRef, Component } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import styled from 'styled-components';
import unescapedText from 'I18n/utils/unescapedText';
import EditForm from 'ContentComponents/components/cmv2/EditForm';
import FieldInheritanceIcon from 'ContentComponents/components/cmv2/FieldInheritanceIcon';
import { Small } from 'foundations-components/transitional/components/Small';
import { H4 } from 'foundations-components/transitional/components/H4';
import SanitizedLabel from 'ContentComponents/components/cmv2/SanitizedLabel';
import NavMarker from 'react-rhumb/NavMarker';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { MediaObject } from 'foundations-components/transitional/components/MediaObject';
import { ScrollingColumn } from 'foundations-components/transitional/components/ScrollingColumn';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Alert } from 'foundations-components/transitional/components/Alert';
import SidebarSection from '../components/sidebar/SidebarSection';
import NavigationBreadcrumb from '../components/themeSettings/NavigationBreadcrumb';
import ThemeSettingsFieldWrapper from '../components/themeSettings/ThemeSettingsFieldWrapper';
import * as ThemeSettingsActions from '../actions/ThemeSettingsActions';
import * as Actions from '../actions/Actions';
import * as PresetActions from '../actions/PresetActions';
import { isEqual } from 'underscore';
import { getDeepValue } from 'ContentUtils/helpers/ObjectHelpers';
import { getSettingsFormSpec, getCanRedo, getCanUndo, getThemeBreakpointStyles, getThemeSettingsValues, getThemeInheritanceDataOverrides, getThemeSettingsRequestStatus, getUniversalThemeSettingsRequestStatus } from '../selectors/themeSettingsSelectors';
import { getBusinessUnitId, getPreviewIframeLoaded, getSelectedBreakpoint } from '../selectors/appSelectors';
import { getActiveFieldKey } from '../selectors/themeSettingsMetaSelectors';
import { getThemeMessages, getThemePath, getThemeResponsiveBreakpointsByName } from '../selectors/themeSelectors';
import { getIsUngatedForPresets, getUserLocale } from '../selectors/authSelectors';
import { getActiveThemePresetId, getActiveThemePresetStyles, getPresetsAsArray, getPresetsRequestStatus, getSelectedThemePresetId } from '../selectors/presetsSelectors';
import { isFulfilled } from '../utils/requestStatus';
import { getClosestNonEmptyParentKey } from '../utils/themeSettings';
import * as CustomWidgetFieldTypes from 'ContentUtils/constants/CustomWidgetFieldTypes';
import { getFieldFromKey } from 'ContentComponents/components/cmv2/utils';
import { MOBILE, TABLET } from '../constants/breakpoints';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { Flex } from 'foundations-components/components/Flex';
import { EllipsesIcon, RefreshIcon } from 'foundations-assets';
import { View } from 'foundations-components/transitional/components/View';
import * as UsageInteractionTypes from '../constants/usage/InteractionTypes';
import { IconButton } from 'foundations-components/transitional/components/IconButton';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { Button } from 'foundations-components/transitional/components/Button';
import { Dropdown } from 'foundations-components/transitional/components/Dropdown';
import ActiveThemePresetCard from '../components/presets/ActiveThemePresetCard';
import PresetListPanel from '../components/presets/PresetListPanel';
import UniversalEditorHeaderContainer from './UniversalEditorHeaderContainer';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const i18nKey = key => `theme-previewer.themeSettings.${key}`;
const AUTOSAVE_DEBOUNCE_PERIOD = 500;
const StyleFieldsLabelWrapper = styled.span.withConfig({
  displayName: "ThemeSettingsContainer__StyleFieldsLabelWrapper",
  componentId: "zzpi5b-0"
})(["word-break:break-all;"]);
const StyleFieldsResetButtonWrapper = styled.span.withConfig({
  displayName: "ThemeSettingsContainer__StyleFieldsResetButtonWrapper",
  componentId: "zzpi5b-1"
})(["display:inline-block;vertical-align:top;"]);
const mapStateToProps = state => {
  const isUngatedForPresets = getIsUngatedForPresets(state);
  return {
    activeThemePresetId: getActiveThemePresetId(state),
    activeThemePresetStyles: getActiveThemePresetStyles(state),
    businessUnitId: getBusinessUnitId(state),
    canRedo: getCanRedo(state),
    canUndo: getCanUndo(state),
    fieldKey: getActiveFieldKey(state),
    formData: getThemeSettingsValues(state),
    formSpec: getSettingsFormSpec(state),
    hasLoadedThemeSettings: isFulfilled(getThemeSettingsRequestStatus(state)),
    hasLoadedUniversalThemeSettings: isFulfilled(getUniversalThemeSettingsRequestStatus(state)),
    previewIframeLoaded: getPreviewIframeLoaded(state),
    selectedBreakpoint: getSelectedBreakpoint(state),
    selectedThemePresetId: getSelectedThemePresetId(state),
    themeBreakpointStyles: getThemeBreakpointStyles(state),
    responsiveBreakpointsByName: getThemeResponsiveBreakpointsByName(state),
    themePath: getThemePath(state),
    themeInheritanceDataOverrides: getThemeInheritanceDataOverrides(state),
    themeMessages: getThemeMessages(state),
    userLocale: getUserLocale(state),
    isUngatedForPresets,
    presets: getPresetsAsArray(state),
    presetsRequestStatus: getPresetsRequestStatus(state)
  };
};
const mapDispatchToProps = {
  onRedoChange: ThemeSettingsActions.redoChange,
  onUndoChange: ThemeSettingsActions.undoChange,
  onUpdateFieldKey: ThemeSettingsActions.updateSettingsFieldKey,
  onUpdateSettings: ThemeSettingsActions.updateSettings,
  onClearSettings: ThemeSettingsActions.clearThemeSettings,
  onResetField: ThemeSettingsActions.resetFieldSettings,
  reloadIframePreview: Actions.reloadIframePreview,
  onFetchPresets: PresetActions.fetchPresets,
  saveSettingsBuffer: ThemeSettingsActions.saveSettingsBuffer,
  fetchHasBufferedSettingsChanges: ThemeSettingsActions.fetchHasBufferedSettingsChanges
};
const connector = connect(mapStateToProps, mapDispatchToProps);

// FIXME: plannning on moving type to util but want to make sure they will be same type on both apps

class ThemeSettingsContainer extends Component {
  constructor(_props) {
    super(_props);
    this.getThemePathFromUrl = () => {
      const path = window.location.pathname;
      const themePath = path.split('/edit/');
      if (themePath && themePath.length > 1) {
        return decodeURI(themePath[1]);
      }
      return null;
    };
    this.resetScrollPosition = () => {
      const columnNode = this._columnRef.current;
      if (columnNode) {
        columnNode.scrollTop = 0;
      }
    };
    this.readFieldKeyCookie = themePath => {
      const sessionFieldKeyMatch = document.cookie.match(new RegExp(`${themePath}=([^;]+)`, 'i'));
      const sessionFieldKey = sessionFieldKeyMatch && sessionFieldKeyMatch[1];
      return sessionFieldKey ? sessionFieldKey.split(',') : null;
    };
    this.setFieldKeyCookie = fieldKey => {
      // Set cookie to expire in 2 hours
      const date = new Date();
      date.setTime(date.getTime() + 2 * 60 * 60 * 1000);
      const expires = `expires=${date.toUTCString()}`;
      document.cookie = `${this.props.themePath}=${fieldKey}; ${expires}; path=/`;
    };
    this.handleRedoChange = () => {
      this.props.onRedoChange();
      this.props.trackUndoRedoInteraction(UsageInteractionTypes.CLICKED_REDO_BUTTON);
    };
    this.handleClearThemeSettings = () => {
      this.props.trackInteraction(UsageInteractionTypes.RESET_THEME_SETTINGS_FORM);
      this.props.onClearSettings();
      this.props.onUpdateFieldKey(null);
    };
    this.handleUndoChange = () => {
      this.props.onUndoChange();
      this.props.trackUndoRedoInteraction(UsageInteractionTypes.CLICKED_UNDO_BUTTON);
    };
    this.handleFieldKeyChange = fieldKey => {
      const {
        clearScrollToThemePath,
        scrollToThemeField
      } = this.props;
      if (scrollToThemeField) clearScrollToThemePath();
      this.props.onUpdateFieldKey(fieldKey);
      this.setFieldKeyCookie(fieldKey);
    };
    this.getTrackingUpdatedValue = value => {
      switch (typeof value) {
        case 'object':
          return JSON.stringify(value);
        default:
          return value.toString();
      }
    };
    this.handleFormUpdate = (key, value, module) => {
      const {
        onUpdateSettings,
        selectedBreakpoint,
        formSpec = {
          fields: []
        },
        saveSettingsBuffer,
        fetchHasBufferedSettingsChanges,
        handleSaveThemeSettingsBuffer,
        isUniversalThemeEditing,
        handleSetIsUniversalThemeSavedAndApplied
      } = this.props;
      const {
        fields
      } = formSpec;
      const field = getFieldFromKey(fields, key) || {};

      //TODO apply tracking later
      if (!isUniversalThemeEditing) {
        this.props.trackUpdateThemeSettings({
          fieldType: field.type,
          fieldPath: key.join('.'),
          value: this.getTrackingUpdatedValue(value),
          themePath: this.getThemePathFromUrl()
        });
      }
      this.setState({
        showApplyButton: true
      });
      onUpdateSettings({
        breakpoint: selectedBreakpoint,
        key,
        themeSettings: module,
        value
      });
      if (isUniversalThemeEditing) {
        //TODO remove ? once props are updated to required
        handleSetIsUniversalThemeSavedAndApplied === null || handleSetIsUniversalThemeSavedAndApplied === void 0 || handleSetIsUniversalThemeSavedAndApplied(false);
        //TODO: look into if we can use debounce here
        setTimeout(() => {
          //this handles auto saving theme settings
          //manual save will be handled in PageSaveButtonContainer
          saveSettingsBuffer({
            isAutoSave: true
          });
          fetchHasBufferedSettingsChanges();
          handleSaveThemeSettingsBuffer === null || handleSaveThemeSettingsBuffer === void 0 || handleSaveThemeSettingsBuffer();
        }, AUTOSAVE_DEBOUNCE_PERIOD);
      }
    };
    this.handleFieldReset = key => {
      const {
        onResetField,
        selectedBreakpoint
      } = this.props;
      onResetField(key, selectedBreakpoint);
    };
    this.handleBrowsePresetsClick = () => {
      this.setState({
        isPresetPanelOpen: true
      });
    };
    this.handleClosePresetPanel = () => {
      this.setState({
        isPresetPanelOpen: false
      });
    };
    this.getTranslatedBreakpointLabel = breakpoint => {
      const {
        themeMessages,
        userLocale
      } = this.props;
      const {
        label: breakpointLabel,
        name
      } = breakpoint;
      const messages = themeMessages && themeMessages.userLocale ? themeMessages[userLocale] : {};
      let label = breakpointLabel;
      if (name === TABLET || name === MOBILE) {
        const breakpointLabelKey = `previewResizeControls.devices.${name}`;
        label = unescapedText(breakpointLabelKey);
      } else {
        const labelTranslationKey = `breakpoints.${name}.label`;
        label = messages[labelTranslationKey] ? messages[labelTranslationKey].message : label;
      }
      return label.toLowerCase();
    };
    this.renderFieldResetButton = labelProps => {
      const {
        formData = {},
        selectedBreakpoint,
        themeBreakpointStyles = {},
        trackInteraction
      } = this.props;
      const {
        inheritedValue,
        isDefaultValue,
        fieldKey,
        onFieldUnlink,
        scrollToField
      } = labelProps;
      const fieldValue = selectedBreakpoint ? getDeepValue(themeBreakpointStyles, [selectedBreakpoint, 'fieldStyles', ...fieldKey]) : getDeepValue(formData, fieldKey);
      const isResettable = fieldValue !== undefined;
      const isOccurence = fieldKey.some(key => typeof key === 'number');
      const onClickHandler = evt => {
        evt.stopPropagation();
        evt.preventDefault();
        const themeSettings = selectedBreakpoint ? getDeepValue(themeBreakpointStyles, [selectedBreakpoint, 'fieldStyles']) : formData;
        const closestNonEmptyParentFieldKey = getClosestNonEmptyParentKey(fieldKey, themeSettings);
        this.setState({
          showApplyButton: true
        });
        this.handleFieldReset(closestNonEmptyParentFieldKey);
      };
      if (inheritedValue) {
        return /*#__PURE__*/_jsx(FieldInheritanceIcon, {
          inheritedValue: inheritedValue,
          isDefaultValue: isDefaultValue,
          onResetClick: evt => {
            trackInteraction(UsageInteractionTypes.RESET_INHERITING_THEME_FIELD);
            onClickHandler(evt);
          },
          onUnlinkClick: onFieldUnlink ? evt => {
            trackInteraction(UsageInteractionTypes.UNLINK_INHERITING_THEME_FIELD);
            onFieldUnlink(evt);
          } : undefined,
          scrollToField: scrollToField
        });
      }
      if (!isResettable || isOccurence) return null;
      return /*#__PURE__*/_jsx(Tooltip, {
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('resetFieldTooltip')
        }),
        children: /*#__PURE__*/_jsx(IconButton, {
          use: "link",
          onClick: evt => {
            trackInteraction(UsageInteractionTypes.RESET_THEME_FIELD);
            onClickHandler(evt);
          },
          marginInlineStart: "100",
          children: /*#__PURE__*/_jsx(RefreshIcon, {
            size: "200",
            color: "icon-interactive-default"
          })
        })
      });
    };
    this.renderFieldLabel = props => {
      const {
        fieldKey,
        fieldType
      } = props;
      const {
        formSpec = {
          fields: []
        }
      } = this.props;
      const {
        fields
      } = formSpec;
      const field = getFieldFromKey(fields, fieldKey) || {};
      return /*#__PURE__*/_jsxs(StyleFieldsLabelWrapper, {
        children: [/*#__PURE__*/_jsx(SanitizedLabel, {
          label: props.label
        }), (fieldType !== CustomWidgetFieldTypes.GROUP || field.occurrence && fieldType === CustomWidgetFieldTypes.GROUP) && /*#__PURE__*/_jsx(StyleFieldsResetButtonWrapper, {
          children: this.renderFieldResetButton(props)
        })]
      });
    };
    this.handleReloadIframePreview = () => {
      const {
        isUniversalThemeEditing,
        reloadScalableEditorIframePreview,
        reloadIframePreview
      } = this.props;
      if (isUniversalThemeEditing) {
        reloadScalableEditorIframePreview === null || reloadScalableEditorIframePreview === void 0 || reloadScalableEditorIframePreview();
      } else {
        reloadIframePreview();
      }
    };
    this._columnRef = /*#__PURE__*/createRef();
    this.state = {
      isPresetPanelOpen: false,
      showApplyButton: false
    };
  }
  componentDidUpdate(prevProps) {
    const {
      fieldKey,
      selectedBreakpoint,
      themePath,
      isUngatedForPresets,
      onFetchPresets
    } = this.props;
    if (themePath !== prevProps.themePath) {
      const sessionFieldKey = this.readFieldKeyCookie(themePath);
      if (sessionFieldKey) {
        this.props.onUpdateFieldKey(sessionFieldKey);
      }
      if (isUngatedForPresets) {
        onFetchPresets(themePath).catch(err => {
          setTimeout(() => {
            throw err;
          });
        });
      }
    }
    if (!isEqual(fieldKey, prevProps.fieldKey)) {
      this.resetScrollPosition();
    }
    if (prevProps.selectedBreakpoint !== selectedBreakpoint) {
      this.handleFieldKeyChange([]);
    }
  }
  get isPresetPanelOpen() {
    const {
      isUngatedForPresets
    } = this.props;
    const {
      isPresetPanelOpen
    } = this.state;
    return isUngatedForPresets && isPresetPanelOpen;
  }
  renderBreadcrumbs() {
    const {
      fieldKey,
      formData = {},
      formSpec = {
        fields: []
      }
    } = this.props;
    const {
      fields
    } = formSpec;
    return /*#__PURE__*/_jsx(NavigationBreadcrumb, {
      fieldKey: fieldKey || [],
      fields: fields,
      module: formData,
      onKeyChange: this.handleFieldKeyChange
    });
  }
  renderResetButton() {
    const dropdownButton = /*#__PURE__*/_jsx(Flex, {
      alignSelf: "center",
      children: /*#__PURE__*/_jsx(IconHolder, {
        marginInlineEnd: "0",
        marginInlineStart: "100",
        marginBlockStart: "100",
        icon: EllipsesIcon,
        size: "600",
        color: "icon-interactive-default",
        paddingBlock: "0"
      })
    });
    return /*#__PURE__*/_jsx(Dropdown, {
      "data-test-id": "reset-to-default-dropdown",
      autoPlacement: "vert",
      buttonUse: "transparent",
      buttonText: dropdownButton,
      buttonStyles: {
        paddingInline: '0',
        paddingBlock: '0'
      },
      caretRenderer: null,
      placement: "bottom left",
      children: /*#__PURE__*/_jsx(Button, {
        "data-test-id": "reset-to-default",
        onClick: this.handleClearThemeSettings,
        use: "link",
        paddingInlineEnd: "200",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('patagoniaResetButton')
        })
      })
    });
  }
  renderBreakpointEditingAlert() {
    const {
      selectedBreakpoint,
      responsiveBreakpointsByName
    } = this.props;
    if (!selectedBreakpoint) return null;
    const breakpoint = responsiveBreakpointsByName[selectedBreakpoint];
    const breakpointLabel = this.getTranslatedBreakpointLabel(breakpoint);
    return /*#__PURE__*/_jsx(Alert, {
      marginBlockStart: "300",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('breakpointsAlert'),
        options: {
          breakpointLabel
        }
      })
    });
  }
  renderHeaderContent() {
    const {
      toggleDependentsPanel,
      isUniversalThemeEditing
    } = this.props;
    if (isUniversalThemeEditing) {
      return /*#__PURE__*/_jsxs("div", {
        children: [this.renderBreadcrumbs(), /*#__PURE__*/_jsx(UniversalEditorHeaderContainer, {
          toggleDependentsPanel: toggleDependentsPanel
        })]
      });
    }
    return /*#__PURE__*/_jsxs("div", {
      children: [this.renderBreadcrumbs(), /*#__PURE__*/_jsx(View, {
        borderBlockEndStyle: "solid",
        borderBlockEndColor: "border-core-default",
        borderBlockEndWidth: "100",
        children: /*#__PURE__*/_jsxs(SidebarSection, {
          children: [/*#__PURE__*/_jsxs(TFlex, {
            align: "stretch",
            direction: "column",
            children: [/*#__PURE__*/_jsx(MediaObject, {
              align: "center",
              itemRight: /*#__PURE__*/_jsx(TFlex, {
                align: "center",
                children: this.renderResetButton()
              }),
              children: /*#__PURE__*/_jsx(H4, {
                marginBlockEnd: "0",
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: i18nKey('header')
                })
              })
            }), this.renderBreakpointEditingAlert()]
          }), /*#__PURE__*/_jsx(View, {
            paddingBlockStart: "500",
            as: "div",
            children: /*#__PURE__*/_jsx(Small, {
              use: "help",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: i18nKey('helpText')
              })
            })
          })]
        })
      })]
    });
  }
  renderApplyButton() {
    const {
      previewIframeLoaded,
      isUniversalThemeEditing,
      isUniversalThemeSaved
    } = this.props;
    const {
      showApplyButton
    } = this.state;
    if (!showApplyButton) return null;

    //theme settings can be saved through theme editor or universal editor
    const showResult = previewIframeLoaded || isUniversalThemeEditing && isUniversalThemeSaved;
    return /*#__PURE__*/_jsxs(View, {
      paddingInline: "600",
      marginBlockStart: "300",
      marginBlockEnd: "600",
      as: "div",
      children: [/*#__PURE__*/_jsx(LoadingSpinner, {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('savingChanges')
        }),
        labelCompletedText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('changesSaved')
        }),
        size: "sm",
        showLabel: true,
        showResult: showResult,
        marginInlineStart: "0",
        paddingBlockStart: "500"
      }), /*#__PURE__*/_jsxs(Small, {
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('cantSeeUpdates')
        }), /*#__PURE__*/_jsx(Button, {
          use: "link",
          onClick: this.handleReloadIframePreview,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('clickToRefresh')
          })
        })]
      })]
    });
  }
  renderPreset() {
    const {
      activeThemePresetId,
      presets,
      presetsRequestStatus,
      fieldKey = [],
      isUngatedForPresets,
      themePath
    } = this.props;
    const isRootSettingView = !fieldKey || fieldKey && !fieldKey.length;
    return isUngatedForPresets && isRootSettingView && presets.length > 0 && /*#__PURE__*/_jsx(View, {
      borderBlockEndStyle: "solid",
      borderBlockEndColor: "border-core-default",
      borderBlockEndWidth: "100",
      children: /*#__PURE__*/_jsx(SidebarSection, {
        children: /*#__PURE__*/_jsx(ActiveThemePresetCard, {
          activeThemePresetId: activeThemePresetId,
          presetsRequestStatus: presetsRequestStatus,
          onBrowsePresetsClick: this.handleBrowsePresetsClick,
          themePath: themePath
        })
      })
    });
  }
  renderPresetListPanel() {
    const {
      activeThemePresetId,
      presets,
      presetsRequestStatus,
      selectedThemePresetId,
      themePath
    } = this.props;
    return /*#__PURE__*/_jsx(PresetListPanel, {
      activeThemePresetId: activeThemePresetId,
      onClose: this.handleClosePresetPanel,
      presets: presets,
      presetsRequestStatus: presetsRequestStatus,
      selectedThemePresetId: selectedThemePresetId,
      themePath: themePath
    });
  }
  renderEditForm() {
    const {
      activeThemePresetStyles,
      businessUnitId,
      fieldKey,
      formData = {},
      formSpec = {
        fields: []
      },
      hasLoadedThemeSettings,
      hasLoadedUniversalThemeSettings,
      isUngatedForPresets,
      scrollToThemeField,
      selectedBreakpoint,
      themeBreakpointStyles,
      themeInheritanceDataOverrides,
      trackInteraction,
      AsyncComponentContext,
      isUniversalThemeEditing,
      hasThemeEditAccess
    } = this.props;
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [!isUniversalThemeEditing && this.renderPreset(), hasLoadedThemeSettings || hasLoadedUniversalThemeSettings ? /*#__PURE__*/_jsxs(SidebarSection, {
        onBoarding: "theme-settings-form",
        testId: "theme-settings-form",
        flush: true,
        children: [/*#__PURE__*/_jsx(NavMarker, {
          name: "THEME_SETTINGS_LOADED",
          children: /*#__PURE__*/_jsx(AsyncComponentContext.Consumer, {
            children: ({
              ThemeMofoFields
            }) => {
              if (!ThemeMofoFields) return null;
              return /*#__PURE__*/_jsx(EditForm, {
                businessUnitId: businessUnitId,
                componentsByType: ThemeMofoFields,
                breakpoint: selectedBreakpoint,
                breakpointStyles: themeBreakpointStyles,
                fieldKey: fieldKey,
                fieldDefaultOverrides: isUngatedForPresets && activeThemePresetStyles ? activeThemePresetStyles : undefined,
                FieldWrapper: ThemeSettingsFieldWrapper,
                flush: false,
                GroupLabelChild: this.renderFieldResetButton,
                hideBackLink: true,
                inheritanceDataOverrides: themeInheritanceDataOverrides,
                id: "theme-settings-form",
                LabelComponent: this.renderFieldLabel,
                lazyRenderGroups: true,
                module: formData,
                moduleSpec: formSpec,
                onFieldKeyChange: this.handleFieldKeyChange,
                onUpdate: this.handleFormUpdate,
                scrollToFieldOnMount: scrollToThemeField && scrollToThemeField.length > 0 ? scrollToThemeField.split('.') : undefined,
                trackFieldAction: trackInteraction,
                readOnly: Boolean(isUniversalThemeEditing && !hasThemeEditAccess)
              });
            }
          })
        }), this.renderApplyButton()]
      }) : /*#__PURE__*/_jsx(LoadingSpinner, {
        grow: true
      })]
    });
  }
  render() {
    return /*#__PURE__*/_jsx(ScrollingColumn, {
      "data-test-id": "theme-settings",
      headerContent: !this.isPresetPanelOpen && this.renderHeaderContent(),
      scrollElementRef: this._columnRef,
      children: this.isPresetPanelOpen ? this.renderPresetListPanel() : this.renderEditForm()
    });
  }
}
export default compose(connect(mapStateToProps, mapDispatchToProps))(ThemeSettingsContainer);